import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Stack,
  Typography,
  ButtonBase,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAppTheme } from '6-shared/ui/theme'
import { zenmoney } from '6-shared/api/zenmoney'
import { tokenStorage } from '6-shared/api/tokenStorage'

import { useAppDispatch } from 'store'
import { setToken } from 'store/token'
import { syncData } from '4-features/sync'
import { loadBackup, loadDemoData, logIn } from '4-features/authorization'

zenmoney.processAuthCode()

export default function Auth() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const theme = useAppTheme()
  const [logoIn, setLogoIn] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [autoLogging, setAutoLogging] = useState(false)
  setTimeout(() => setLogoIn(true), 300)

  // Auto-login from env token: if REACT_APP_DEV_TOKEN is available
  // and user hasn't explicitly logged out, skip the auth screen entirely.
  useEffect(() => {
    const token = tokenStorage.get()
    if (token) {
      setAutoLogging(true)
      dispatch(setToken(token))
      dispatch(syncData())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const parseFiles = (fileList: FileList) => dispatch(loadBackup(fileList[0]))

  const dragOverStyle = {
    background: theme.palette.action.focus,
    transform: 'scale(1.1)',
    transition: `300ms ${theme.transitions.easing.easeInOut}`,
  }
  const defaultStyle = {
    transform: 'scale(1)',
    transition: `300ms ${theme.transitions.easing.easeInOut}`,
  }
  // When auto-logging, show a spinner instead of the login form
  if (autoLogging) {
    return (
      <Box sx={{ display: 'grid', placeContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Stack
      spacing={8}
      style={isDragging ? dragOverStyle : defaultStyle}
      onDragOver={e => {
        e.stopPropagation()
        e.preventDefault()
      }}
      onDragEnter={e => {
        e.stopPropagation()
        e.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={e => {
        e.stopPropagation()
        e.preventDefault()
        setIsDragging(false)
      }}
      onDrop={e => {
        e.stopPropagation()
        e.preventDefault()
        parseFiles(e?.dataTransfer?.files)
      }}
      sx={{
        p: 3,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <Typography
        variant="h2"
        sx={{
          fontWeight: 800,
          color: theme.palette.primary.main,
          opacity: logoIn ? 1 : 0,
          transition: '600ms ease-in-out',
        }}
      >
        Endless
      </Typography>
      <Stack
        spacing={3}
        sx={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <Fade in timeout={1000}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => dispatch(logIn('ru'))}
            children={t('btnLogin')}
          />
        </Fade>

        <Fade in timeout={2000}>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {t('haveTrouble')}{' '}
            <ButtonBase
              onClick={() => dispatch(logIn('app'))}
              sx={{
                p: 1,
                m: -1,
                verticalAlign: 'baseline',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
                borderRadius: 1,
                color: theme.palette.primary.main,
                '&:hover': {
                  color: theme.palette.secondary.main,
                },
                '&:focus': {
                  color: theme.palette.secondary.main,
                },
              }}
            >
              {t('btnAlternativeSignIn')}
            </ButtonBase>
          </Typography>
        </Fade>

        <Fade in timeout={3000}>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              color="primary"
              size="large"
              onClick={() => dispatch(loadDemoData())}
            >
              {t('btnDemoMode')}
            </Button>
          </Box>
        </Fade>
      </Stack>
    </Stack>
  )
}
